// src/pages/markets/board/state/profileSerde.ts
//
// Every profile rule that does not need a browser or Firestore.

import { FILTER_WIRE_KEYS, GATED_FIELDS, type BoardFilters } from '../api/boardTypes';
import { BOARD_COLUMNS, type BodyMode, type CardView } from '../board/columnConfig';
import type { ProfileColumn } from './profileTypes';
import type { StoredColumn, StoredPreferences } from './usePreferences';

const NON_FILTER_KEYS = ['body', 'view', 'source'] as const;


// Lives here, not in usePreferences, so the pure module stays free of
// react-admin — importing it the other way drags the MUI ESM cascade into tests.
export function filtersToStored(f: BoardFilters): Partial<StoredColumn> {
    const out: Partial<StoredColumn> = {
        events_include: f.eventsInclude ?? [],
        events_exclude: f.eventsExclude ?? [],
        tags_include: f.tagsInclude ?? [],
        tags_exclude: f.tagsExclude ?? [],
        tag_match: f.tagMatch,
        group: f.group,
        sort_by: f.sortBy,
        sort_dir: f.sortDir,
    };
    // Firestore rejects undefined, and a column that does not declare a field
    // must not write a null key that would read back as a cleared filter.
    for (const field of GATED_FIELDS) {
        const value = f[field];
        if (value !== undefined) out[FILTER_WIRE_KEYS[field]] = value;
    }
    return out;
}

export function columnsFromBoard(
    order: readonly string[],
    filters: Record<string, BoardFilters>,
    layout: Record<string, { body: BodyMode; view: CardView }>,
    sources: Record<string, string>,
): ProfileColumn[] {
    return order.map(id => ({
        id,
        source: sources[id] ?? id,
        filters: filtersToStored(filters[id]) as Record<string, unknown>,
        layout: { body: layout[id]?.body ?? null, view: layout[id]?.view ?? null },
    }));
}

// The stored map keyed by column id, as written before profiles existed.
export function migrateColumns(stored: StoredPreferences): ProfileColumn[] {
    return BOARD_COLUMNS.map(config => {
        const bag = { ...(stored.columns[config.id] ?? {}) } as Record<string, unknown>;
        const body = (bag.body as BodyMode | undefined) ?? null;
        const view = (bag.view as CardView | undefined) ?? null;
        const source = (bag.source as string | undefined) ?? config.id;
        for (const key of NON_FILTER_KEYS) delete bag[key];
        return {
            id: config.id,
            source,
            filters: bag,
            layout: { body, view },
        };
    });
}

function bagsEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
        if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return false;
    }
    return true;
}

// Compared over the union of keys, so a gated field absent on both sides is not a difference.
export function columnsAreEqual(a: ProfileColumn[], b: ProfileColumn[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((col, i) => {
        const other = b[i];
        return (
            col.id === other.id &&
            col.source === other.source &&
            col.layout.body === other.layout.body &&
            col.layout.view === other.layout.view &&
            bagsEqual(col.filters, other.filters)
        );
    });
}
